import React, { useState, useEffect, useCallback } from 'react';
import NavBar from '../components/Navbar/NavBar';
import YoutubePlayer from './YoutubePlayer';
import ContactModal from './ContactModal';
import DonationModal from './DonationModal';

// Import volunteer images
import img1 from '../images/volunteers/volunteers_1.jpg';
import img2 from '../images/volunteers/volunteers_2.jpg';
import img3 from '../images/volunteers/volunteers_3.jpg';
import img4 from '../images/volunteers/volunteers_4.jpg';

// Import horse images for donations section
import horse1 from '../images/horses/Buckwheat.jpg';
import horse2 from '../images/horses/Dixie.jpg';
import horse3 from '../images/horses/Doc.jpg';
import horse4 from '../images/horses/Nathan.jpg';
import horse5 from '../images/horses/Norton.jpg';

const Hero = () => {
  const [currentImage, setCurrentImage] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const images = [img3, img4, img2, img1];

  // Horse carousel state for donations section
  const [currentHorseImage, setCurrentHorseImage] = useState(0);
  const [isHorseAutoPlaying, setIsHorseAutoPlaying] = useState(true);
  const horseImages = [horse1, horse2, horse3, horse4, horse5];

  // Contact modal state
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);
  const [contactModalSubject, setContactModalSubject] = useState('');
  const [contactModalTopic, setContactModalTopic] = useState('');

  // Donation modal state
  const [isDonationModalOpen, setIsDonationModalOpen] = useState(false);

  const handleLearnMore = (e) => {
    e.preventDefault();
    setContactModalSubject('');
    setContactModalTopic('Program Information');
    setIsContactModalOpen(true);
  };

  const handleVolunteerClick = (e) => {
    e.preventDefault();
    setContactModalSubject('');
    setContactModalTopic('Volunteer Request');
    setIsContactModalOpen(true);
  };

  const closeContactModal = () => {
    setIsContactModalOpen(false);
    setContactModalSubject('');
    setContactModalTopic('');
  };

  const handleDonationClick = (e) => {
    e.preventDefault();
    setIsDonationModalOpen(true);
  };

  const closeDonationModal = () => {
    setIsDonationModalOpen(false);
  };

  const nextImage = useCallback(() => {
    setCurrentImage((prevImage) => (prevImage + 1) % images.length);
  }, [images.length]);

  const prevImage = useCallback(() => {
    setCurrentImage((prevImage) => (prevImage - 1 + images.length) % images.length);
  }, [images.length]);

  const nextHorseImage = useCallback(() => {
    setCurrentHorseImage((prevImage) => (prevImage + 1) % horseImages.length);
  }, [horseImages.length]);

  const prevHorseImage = useCallback(() => {
    setCurrentHorseImage((prevImage) => (prevImage - 1 + horseImages.length) % horseImages.length);
  }, [horseImages.length]);

  useEffect(() => {
    let intervalId;
    if (isAutoPlaying) {
      intervalId = setInterval(() => {
        nextImage();
      }, 5000);
    }
    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [isAutoPlaying, nextImage]);

  useEffect(() => {
    let horseIntervalId;
    if (isHorseAutoPlaying) {
      horseIntervalId = setInterval(() => {
        nextHorseImage();
      }, 5000);
    }
    return () => {
      if (horseIntervalId) {
        clearInterval(horseIntervalId);
      }
    };
  }, [isHorseAutoPlaying, nextHorseImage]);

  const handleMouseEnter = () => setIsAutoPlaying(false);
  const handleMouseLeave = () => setIsAutoPlaying(true);

  const handleHorseMouseEnter = () => setIsHorseAutoPlaying(false);
  const handleHorseMouseLeave = () => setIsHorseAutoPlaying(true);

  return (
    <>
      {/* Navbar - separate from hero background */}
      <NavBar />
      
      {/* Hero Section */}
      <div className="hero" id="hero">
        <div className="hero-content flex items-center justify-center">
          <div className="container mx-auto px-4 sm:px-6 pt-36 sm:pt-40 pb-6 sm:pb-16 relative z-10">
            {/* Transparent glass-section to match other sections' structure */}
            <div className="bg-transparent backdrop-blur-0 border-0 rounded-0 shadow-none">
              
              {/* Mobile Layout - Stack vertically with video in middle */}
              <div className="lg:hidden">
                {/* Title Section */}
                <div className="text-center mb-6">
                  <div className="mb-4">
                    <h1 className="text-2xl font-bold text-white drop-shadow-lg leading-tight" 
                        style={{textShadow: '2px 2px 4px rgba(0,0,0,0.8)'}}>
                      Therapeutic Riding at Normandy Farm
                    </h1>
        </div>

                  <h2 className="text-xl font-bold text-white mb-6 leading-tight drop-shadow-2xl" style={{textShadow: '2px 2px 4px rgba(0,0,0,0.8), 0px 0px 8px rgba(0,0,0,0.6)'}}>
                    Empowering lives through the{' '}
                    <span className="text-green-300 drop-shadow-2xl" style={{textShadow: '2px 2px 4px rgba(0,0,0,0.9), 0px 0px 8px rgba(0,0,0,0.7)'}}>healing power of horses.</span>
                  </h2>
                </div>

                {/* Video Section - Mobile */}
                <div className="flex justify-center mb-6">
                  <div className="relative max-w-lg w-full">
                    <div className="relative bg-white/10 backdrop-blur-lg rounded-2xl shadow-2xl overflow-hidden border border-white/20 p-3">
                      <div className="relative w-full mb-3" style={{ paddingBottom: '56.25%' }}>
                        <div className="absolute inset-0">
                          <YoutubePlayer
                            videoId="QprhXgd5HP8"
                            className="w-full h-full object-cover rounded-xl"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Description Card - Mobile */}
                <div className="text-center">
                  <div className="bg-black/20 backdrop-blur-sm rounded-2xl p-4 mb-6 border border-white/10">
                    <p className="text-base text-white mb-4 leading-relaxed drop-shadow-lg">
                      Since 1992, Therapeutic Riding at Normandy Farm has brought hope and healing to Harford County, Maryland, transforming lives by building restorative bonds with horses.
                    </p>

                    {/* Key Features */}
                    <div className="space-y-3">
                      <div className="flex items-center text-white">
                        <svg className="w-5 h-5 text-green-300 mr-3 flex-shrink-0 drop-shadow-lg" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>                        
                        <span className="text-sm drop-shadow-md">Safe, outdoor arena setting</span>
                      </div>
                      <div className="flex items-center text-white">
                        <svg className="w-5 h-5 text-green-300 mr-3 flex-shrink-0 drop-shadow-lg" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                        <span className="text-sm drop-shadow-md">Individual and group sessions</span>
                      </div>
                      <div className="flex items-center text-white">
                        <svg className="w-5 h-5 text-green-300 mr-3 flex-shrink-0 drop-shadow-lg" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                        <span className="text-sm drop-shadow-md">Experienced and caring volunteers</span>
                      </div>
                    </div>
                  </div>

                  {/* Call to Action Buttons - Mobile */}
                  <div className="flex flex-col gap-3 justify-center items-center w-full">
                    <button
                      onClick={handleLearnMore}
                      className="w-full bg-green-600 hover:bg-green-700 text-white font-semibold py-3 px-6 rounded-full text-base transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl drop-shadow-lg min-h-[48px]"
                    >
                      Contact Us →
                    </button>

                    <button
                      onClick={handleDonationClick}
                      className="w-full bg-green-600 hover:bg-green-700 text-white font-semibold py-3 px-6 rounded-full text-base transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl drop-shadow-lg flex items-center justify-center min-h-[48px]"
                    >
                      Donate Now
                      <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                      </svg>
                    </button>
                  </div>
                </div>
              </div>

              {/* Desktop Layout - Side by side */}
              <div className="hidden lg:grid grid-cols-2 gap-8 lg:gap-12 items-center">
                
                {/* Main Content - Desktop */}
                <div className="text-left">
                  <div className="flex flex-col items-start mb-6 sm:mb-8">
                    <div className="mb-4 sm:mb-6">
                      <h1 className="text-4xl lg:text-5xl xl:text-6xl font-bold text-white drop-shadow-lg leading-tight" 
                          style={{textShadow: '2px 2px 4px rgba(0,0,0,0.8)'}}>
                        Therapeutic Riding at Normandy Farm
              </h1>
                    </div>
                    
                    <h2 className="text-3xl lg:text-4xl xl:text-5xl font-bold text-white mb-4 sm:mb-6 leading-tight drop-shadow-2xl" style={{textShadow: '2px 2px 4px rgba(0,0,0,0.8), 0px 0px 8px rgba(0,0,0,0.6)'}}>
                      Empowering lives through the{' '}
                      <span className="text-green-300 drop-shadow-2xl" style={{textShadow: '2px 2px 4px rgba(0,0,0,0.9), 0px 0px 8px rgba(0,0,0,0.7)'}}>healing power of horses.</span>
                    </h2>
                    
                    {/* Description with subtle background - Desktop */}
                    <div className="bg-black/20 backdrop-blur-sm rounded-2xl p-6 mb-6 sm:mb-8 border border-white/10">
                      <p className="text-xl lg:text-2xl text-white mb-4 sm:mb-6 max-w-2xl leading-relaxed drop-shadow-lg">
                        Since 1992, Therapeutic Riding at Normandy Farm has brought hope and healing to Harford County, Maryland, transforming lives by building restorative bonds with horses.
                      </p>

                      {/* Key Features - Desktop */}
                      <div className="space-y-3 sm:space-y-4">
                        <div className="flex items-center text-white">
                          <svg className="w-6 h-6 text-green-300 mr-3 flex-shrink-0 drop-shadow-lg" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                          <span className="text-lg lg:text-xl drop-shadow-md">Comprehensive therapeutic programs</span>
                        </div>
                        <div className="flex items-center text-white">
                          <svg className="w-6 h-6 text-green-300 mr-3 flex-shrink-0 drop-shadow-lg" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                          <span className="text-lg lg:text-xl drop-shadow-md">Safe, indoor arena setting</span>
                        </div>
                        <div className="flex items-center text-white">
                          <svg className="w-6 h-6 text-green-300 mr-3 flex-shrink-0 drop-shadow-lg" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                          <span className="text-lg lg:text-xl drop-shadow-md">Experienced and caring volunteers</span>
                        </div>
                      </div>
                    </div>

                    {/* Call to Action Buttons - Desktop */}
                    <div className="flex flex-row gap-4 justify-start items-center">
                      <button
                        onClick={handleLearnMore}
                        className="bg-green-600 hover:bg-green-700 text-white font-semibold py-4 px-8 rounded-full text-lg transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl drop-shadow-lg min-h-[48px]"
                      >
                        Contact Us →
                      </button>

                      <button
                        onClick={handleDonationClick}
                        className="bg-green-600 hover:bg-green-700 text-white font-semibold py-4 px-8 rounded-full text-lg transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl drop-shadow-lg flex items-center justify-center min-h-[48px]"
                      >
                        Donate Now
                        <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                        </svg>
                      </button>
                    </div>
                  </div>
                </div>

                {/* Video Section - Desktop */}
                <div className="flex justify-end">
                  <div className="relative max-w-2xl w-full">
                    <div className="relative bg-white/10 backdrop-blur-lg rounded-3xl shadow-2xl overflow-hidden border border-white/20 p-6">
                      <div className="relative w-full mb-4" style={{ paddingBottom: '56.25%' }}>
                        <div className="absolute inset-0">
                          <YoutubePlayer
                            videoId="QprhXgd5HP8"
                            className="w-full h-full object-cover rounded-2xl"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

              </div>
            </div>
          </div>
        </div>

        {/* About Us Section - Mobile optimized */}
        <div id="about" className="container mx-auto px-4 sm:px-6 pt-6 pb-6 sm:py-16 relative z-10">
          <div className="glass-section">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center">
              
              {/* Volunteer Image Carousel */}
              <div 
                className="relative order-2 lg:order-1"
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
              >
                <div className="relative overflow-hidden rounded-2xl shadow-2xl">
                <div className="relative w-full" style={{ paddingBottom: '56.25%' }}>
                    {images.map((img, index) => (
                      <img
                        key={index}
                        alt={`volunteers ${index + 1}`}
                        className={`absolute top-0 left-0 w-full h-full object-cover transition-opacity duration-500 ease-in-out ${
                          index === currentImage ? 'opacity-100 z-10' : 'opacity-0 z-0'
                        }`}
                        src={img}
                      />
                    ))}
                  </div>
                  {/* Navigation Buttons - Touch friendly */}
                  <div className="absolute top-1/2 transform -translate-y-1/2 left-0 right-0 flex justify-between px-2 sm:px-4 z-20">
                    <button
                      onClick={prevImage}
                      className="bg-black/50 text-white p-2 sm:p-3 rounded-full hover:bg-black/70 transition-all duration-300 backdrop-blur-sm min-w-[44px] min-h-[44px] flex items-center justify-center"
                    >
                      &#10094;
                    </button>
                    <button
                      onClick={nextImage}
                      className="bg-black/50 text-white p-2 sm:p-3 rounded-full hover:bg-black/70 transition-all duration-300 backdrop-blur-sm min-w-[44px] min-h-[44px] flex items-center justify-center"
                    >
                      &#10095;
                    </button>
                  </div>
                </div>
              </div>

              {/* About Us Content */}
              <div className="text-white order-1 lg:order-2">
                <h1 className="text-4xl sm:text-3xl lg:text-4xl font-bold mb-4 sm:mb-6 drop-shadow-xl text-center">
                  Our Community Impact
                </h1>
                
                <div className="space-y-4 sm:space-y-6">
                  <p className="text-base sm:text-lg lg:text-xl leading-relaxed drop-shadow-lg text-center lg:text-justify">
                    Therapeutic Riding at Normandy Farm (TRNF) guides riders through a comprehensive program of warm-ups, riding skills, horse care, and
                  fun activities in a safe, indoor arena setting.
                </p>
                  
                  <p className="text-base sm:text-lg lg:text-xl leading-relaxed drop-shadow-lg text-center lg:text-justify">
                  Our team includes both seasoned individuals with years of experience and those with no prior
                  experience, united by a willingness to contribute to their community.
                </p>

                  {/* Impact Stats - Mobile optimized */}
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6 mt-6 sm:mt-8 mb-6 sm:mb-8">
                    <div className="text-center bg-white/10 rounded-xl p-3 sm:p-4 backdrop-blur-sm">
                      <div className="text-2xl sm:text-3xl font-bold text-green-300 mb-1 sm:mb-2 drop-shadow-lg">30+</div>
                      <div className="text-xs sm:text-sm drop-shadow-md">Years of Service</div>
                    </div>
                    <div className="text-center bg-white/10 rounded-xl p-3 sm:p-4 backdrop-blur-sm">
                      <div className="text-2xl sm:text-3xl font-bold text-green-300 mb-1 sm:mb-2 drop-shadow-lg">50+</div>
                      <div className="text-xs sm:text-sm drop-shadow-md">Volunteers</div>
                    </div>
                    <div className="text-center bg-white/10 rounded-xl p-3 sm:p-4 backdrop-blur-sm">
                      <div className="text-2xl sm:text-3xl font-bold text-green-300 mb-1 sm:mb-2 drop-shadow-lg">500+</div>
                      <div className="text-xs sm:text-sm drop-shadow-md">Lives Touched</div>
                    </div>
                  </div>

                  {/* Program Components - Mobile optimized */}
                  <div id="services" className="mt-6 sm:mt-8">
                    <h1 className="text-4xl sm:text-3xl lg:text-4xl font-bold mb-4 sm:mb-6 drop-shadow-xl text-center">Our Program Components</h1>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
                      
                      {/* Warm-up Exercises */}
                      <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 sm:p-6 border border-white/20">
                        <div className="text-center">
                          <div className="w-10 h-10 sm:w-12 sm:h-12 mx-auto mb-3 sm:mb-4 bg-green-300/20 rounded-full flex items-center justify-center">
                            <svg className="w-5 h-5 sm:w-6 sm:h-6 text-green-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                            </svg>
                          </div>
                          <h4 className="text-base sm:text-lg font-semibold mb-2 sm:mb-3 drop-shadow-md text-white">Warm-up Exercises</h4>
                          <p className="text-xs sm:text-sm drop-shadow-md text-white">
                            Lessons begin with instructor-led basic stretching followed by light warm-up exercises to prepare riders.
                          </p>
                        </div>
                      </div>

                      {/* Horsecare Training */}
                      <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 sm:p-6 border border-white/20">
                        <div className="text-center">
                          <div className="w-10 h-10 sm:w-12 sm:h-12 mx-auto mb-3 sm:mb-4 bg-green-300/20 rounded-full flex items-center justify-center">
                            <svg className="w-5 h-5 sm:w-6 sm:h-6 text-green-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                            </svg>
                          </div>
                          <h4 className="text-base sm:text-lg font-semibold mb-2 sm:mb-3 drop-shadow-md text-white">Horsecare Training</h4>
                          <p className="text-xs sm:text-sm drop-shadow-md text-white">
                            Learn horse anatomy, grooming, feeding, and saddling fundamentals before riding instruction.
                          </p>
                        </div>
                      </div>

                      {/* Horseback Riding */}
                      <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 sm:p-6 border border-white/20 sm:col-span-2 lg:col-span-1">
                        <div className="text-center">
                          <div className="w-10 h-10 sm:w-12 sm:h-12 mx-auto mb-3 sm:mb-4 bg-green-300/20 rounded-full flex items-center justify-center">
                            <svg className="w-5 h-5 sm:w-6 sm:h-6 text-green-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.828 14.828a4 4 0 01-5.656 0M9 10h1.5a4.5 4.5 0 100 9H9V10z" />
                            </svg>
                          </div>
                          <h4 className="text-base sm:text-lg font-semibold mb-2 sm:mb-3 drop-shadow-md text-white">Horseback Riding</h4>
                          <p className="text-xs sm:text-sm drop-shadow-md text-white">
                            Fun activities including horseback games, obstacle courses, and gymnastic movements on horseback.
                          </p>
                        </div>
                      </div>

                    </div>
                  </div>
                </div>
              </div>

            </div>
          </div>
        </div>

        {/* Volunteer and Events Section - Mobile optimized */}
        <div className="container mx-auto px-4 sm:px-6 pt-6 pb-6 sm:py-16 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 sm:gap-10 items-start">
            
            {/* Volunteer Section */}
            <div id="volunteer" className="glass-section">
              <div className="text-white">
                <h1 className="text-4xl sm:text-3xl lg:text-4xl font-bold mb-4 sm:mb-6 drop-shadow-xl text-center">Volunteering</h1>
                <div className="space-y-4 sm:space-y-6">
                  <p className="text-lg sm:text-xl lg:text-2xl leading-relaxed drop-shadow-lg text-center">
                    <span className="text-green-300 drop-shadow-2xl" style={{textShadow: '2px 2px 4px rgba(0,0,0,0.9), 0px 0px 8px rgba(0,0,0,0.7)'}}>The success of TRNF relies on enthusiastic volunteers!</span>
                  </p>
                  <p className="text-base sm:text-lg lg:text-xl leading-relaxed drop-shadow-lg text-center lg:text-justify">
                    Ways you can help include serving as a horse leader, a side walker who assists riders with balance
                    and riding instructions, or lending a hand with grooming and caring of the horses.
                  </p>
                  
                  <p className="text-base sm:text-lg lg:text-xl leading-relaxed drop-shadow-lg text-center lg:text-justify">
                    All volunteers receive in-service training to better support the special needs of our riders.
                  </p>

                  {/* Volunteer Button - Mobile optimized */}
                  <div className="flex justify-center pt-2">
                    <button
                      onClick={handleVolunteerClick}
                      className="w-full sm:w-auto bg-green-600 hover:bg-green-700 text-white font-semibold py-3 sm:py-4 px-6 sm:px-8 rounded-full text-base sm:text-lg transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl drop-shadow-lg flex items-center justify-center min-h-[48px]"
                    >
                      Volunteer With Us
                      <svg className="w-4 h-4 sm:w-5 sm:h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                  </svg>
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Events/Calendar Section - Mobile optimized */}
            <div id="events" className="glass-section">
              <div className="text-white">
                <h1 className="text-4xl sm:text-3xl lg:text-4xl font-bold mb-4 sm:mb-6 drop-shadow-xl text-center">Upcoming Events</h1>                
                {/* Calendar Container - Mobile optimized */}
                <div className="calendar-container relative pb-[112%] sm:pb-[75%] h-0 overflow-hidden rounded-xl bg-white/10 backdrop-blur-sm border border-white/20">
                  <iframe
                    src="https://calendar.google.com/calendar/embed?height=600&wkst=1&bgcolor=%23ffffff&ctz=America%2FNew_York&src=trnfinc%40gmail.com&color=%23039BE5&showTitle=0&showNav=1&showDate=1&showPrint=0&showTabs=1&showCalendars=0&showTz=0&mode=AGENDA"
                    className="absolute top-0 left-0 w-full h-full rounded-xl"
                    frameBorder="0"
                    scrolling="no"
                    title="Horse Farm Events Schedule"
                  ></iframe>
                </div>
                
                <p className="text-lg lg:text-lg text-white/80 text-center mt-3 lg:mt-4 drop-shadow-md">
                  Stay updated with our latest events and activities!
                </p>
              </div>
            </div>

          </div>
        </div>

        {/* Donations Section - Mobile optimized */}
        <div id="donations" className="container mx-auto px-4 sm:px-6 pt-6 pb-6 sm:py-16 relative z-10">
          <div className="glass-section">
            <div className="text-white text-center mb-6 sm:mb-8">
              <h1 className="text-4xl sm:text-3xl lg:text-4xl font-bold mb-4 sm:mb-6 drop-shadow-xl text-center">Donations</h1>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center">
              
              {/* Horse Image Carousel - Mobile optimized */}
              <div 
                className="relative order-2 lg:order-1 w-full sm:w-3/4 lg:w-2/3 mx-auto"
                onMouseEnter={handleHorseMouseEnter}
                onMouseLeave={handleHorseMouseLeave}
              >
                <div className="relative overflow-hidden rounded-2xl shadow-2xl">
                  <div className="relative w-full pt-[133%]">
                    {horseImages.map((img, index) => (
                      <img
                        key={index}
                        alt={`horse ${index + 1}`}
                        className={`absolute top-0 left-0 w-full h-full object-cover transition-opacity duration-500 ease-in-out ${
                          index === currentHorseImage ? 'opacity-100 z-10' : 'opacity-0 z-0'
                        }`}
                        src={img}
                      />
                    ))}
                  </div>
                  {/* Navigation Buttons - Touch friendly */}
                  <div className="absolute top-1/2 transform -translate-y-1/2 left-0 right-0 flex justify-between px-2 sm:px-4 z-20">
                    <button
                      onClick={prevHorseImage}
                      className="bg-black/50 text-white p-2 sm:p-3 rounded-full hover:bg-black/70 transition-all duration-300 backdrop-blur-sm min-w-[44px] min-h-[44px] flex items-center justify-center"
                    >
                      &#10094;
                    </button>
                    <button
                      onClick={nextHorseImage}
                      className="bg-black/50 text-white p-2 sm:p-3 rounded-full hover:bg-black/70 transition-all duration-300 backdrop-blur-sm min-w-[44px] min-h-[44px] flex items-center justify-center"
                    >
                      &#10095;
                    </button>
                  </div>
                </div>
              </div>

              {/* Donations Content - Mobile optimized */}
              <div className="text-white order-1 lg:order-2">
                <h3 className="text-lg sm:text-xl lg:text-2xl xl:text-3xl font-bold mb-4 sm:mb-6 drop-shadow-xl text-center">
                  <span className="text-green-300 drop-shadow-2xl" style={{textShadow: '2px 2px 4px rgba(0,0,0,0.9), 0px 0px 8px rgba(0,0,0,0.7)'}}>Teddy Roosevelt once said, "Nothing does as much for the inside of a man as the outside of a horse."</span>
                </h3>
                
                <div className="space-y-4 sm:space-y-6">
                  <p className="text-base sm:text-lg lg:text-xl leading-relaxed drop-shadow-lg text-center lg:text-justify">
                    Therapeutic riding benefits people with special needs in countless ways—both inside and out! 
                    Horses provide a profound sense of independence and equine activities foster self-awareness, 
                    build self-confidence, and enhance concentration. They also improve posture, balance, 
                    coordination, strength, and flexibility!
                  </p>
                  
                  <p className="text-base sm:text-lg lg:text-xl leading-relaxed drop-shadow-lg text-center lg:text-justify">
                    Donate today to help continue providing the benefits of therapeutic riding to residents of Harford County.
                  </p>

                  {/* Donate Button - Mobile optimized */}
                  <div className="flex justify-center pt-2">
                    <button
                      onClick={handleDonationClick}
                      className="w-full sm:w-auto bg-green-600 hover:bg-green-700 text-white font-semibold py-3 sm:py-4 px-6 sm:px-8 rounded-full text-base sm:text-lg transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl drop-shadow-lg flex items-center justify-center min-h-[48px]"
                    >
                      Donate To Our Cause
                      <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                      </svg>
                    </button>
                  </div>
                </div>
              </div>

            </div>
          </div>
        </div>

        {/* Footer Section - Mobile optimized */}
        <div className="container mx-auto px-4 sm:px-6 py-6 sm:py-8 relative z-10">
          <div className="text-center">
            <div className="text-xs sm:text-sm text-white font-semibold drop-shadow-lg">
              Copyright &copy; {new Date().getFullYear()}{"  "}
              <span className="hover:text-white/80 transition-colors">
                Therapy Riding at Normandy Farm
              </span>. All rights reserved.
            </div>
          </div>
        </div>
      </div>

      {/* Contact Modal */}
      {isContactModalOpen && (
        <ContactModal
          isOpen={isContactModalOpen}
          onClose={closeContactModal}
          defaultSubject={contactModalSubject}
          defaultTopic={contactModalTopic}
        />
      )}

      {/* Donation Modal */}
      {isDonationModalOpen && (
        <DonationModal
          isOpen={isDonationModalOpen}
          onClose={closeDonationModal}
        />
      )}
    </>
  );
};

export default Hero;
